import { CameraInfo, CameraType, SizeInfo } from '../service/hw-service-model';

const YUYV_PHOTO_AREA_THRESHOLD = 0.2;

export class CameraSettings {
  private static cameraSelections = new Map<ComponentType, number>();
  private static lightModeSelections = new Map<ComponentType, boolean>();

  static isLightSupported(camera: CameraType): boolean {
    return camera === CameraType.Back || camera === CameraType.Primary;
  }

  static getLightMode(component: ComponentType, selectedCamera: CameraInfo): boolean {
    if (this.lightModeSelections.has(component) && CameraSettings.isLightSupported(selectedCamera.type)) {
      return this.lightModeSelections.get(component)!;
    } else {
      this.lightModeSelections.set(component, false);
      return false;
    }
  }

  static setLightMode(key: ComponentType, isActivated: boolean): void {
    this.lightModeSelections.set(key, isActivated);
  }

  static selectCamera(key: ComponentType, list: CameraInfo[], defaultCameraType: CameraType): CameraInfo | null {
    let serviceableCameraList = list.filter((c) => !!c.frames && c.frames.length > 0);
    if (serviceableCameraList.length === 0) {
      return null;
    }

    let camera: CameraInfo | undefined = undefined;
    if (this.cameraSelections.has(key)) {
      let lastSelectedCameraId = this.cameraSelections.get(key);
      camera = serviceableCameraList.find((c) => c.id === lastSelectedCameraId);
    } else {
      camera = serviceableCameraList.find((c) => c.type === defaultCameraType);
    }

    if (camera === undefined) {
      camera = serviceableCameraList[0];
    }

    this.cameraSelections.set(key, camera.id);
    return camera;
  }

  static selectAnotherCamera(key: ComponentType, list: CameraInfo[], currentCameraId: number): CameraInfo | null {
    let serviceableCameraList = list.filter((c) => !!c.frames && c.frames.length > 0);
    if (serviceableCameraList.length === 0) {
      return null;
    }

    let camera = serviceableCameraList.find((c) => c.type !== currentCameraId);
    if (camera === undefined) {
      return null;
    }

    this.cameraSelections.set(key, camera.id);
    return camera;
  }

  static selectFrameByFormatThenArea(list: SizeInfo[], format: 'JPEG' | 'YUYV', area: number): SizeInfo {
    const res = list.sort((a, b) => {
      // format
      if (a.format === format && b.format !== format) {
        return -1;
      }
      if (a.format !== format && b.format === format) {
        return 1;
      }
      // area
      return Math.abs(a.area - area) - Math.abs(b.area - area);
    });
    return res[0];
  }

  static selectFrameForPortrait(
    list: SizeInfo[],
    width: number,
    height: number,
    multJPEG: number,
    multYUYV: number,
    dislikeYUYV: boolean,
  ): SizeInfo {
    const res = list.sort((a, b) => {
      // YUYV compatibility
      const bestAreaForYUYV = width * height * multYUYV * multYUYV;
      const aIsCompatible = a.format !== 'YUYV' || (!dislikeYUYV && a.isAreaNear(bestAreaForYUYV, YUYV_PHOTO_AREA_THRESHOLD));
      const bIsCompatible = b.format !== 'YUYV' || (!dislikeYUYV && b.isAreaNear(bestAreaForYUYV, YUYV_PHOTO_AREA_THRESHOLD));
      if (aIsCompatible && !bIsCompatible) {
        return -1;
      }
      if (!aIsCompatible && bIsCompatible) {
        return 1;
      }
      // ratio
      if (a.width * height === width * a.height && b.width * height !== width * b.height) {
        return -1;
      }
      if (a.width * height !== width * a.height && b.width * height === width * b.height) {
        return 1;
      }
      // format
      if (a.format === 'YUYV' && b.format !== 'YUYV') {
        return -1;
      }
      if (a.format !== 'YUYV' && b.format === 'YUYV') {
        return 1;
      }
      // area
      const bestAreaForJPEG = width * height * multJPEG * multJPEG;
      const bestAreaForA = a.format !== 'YUYV' ? bestAreaForJPEG : bestAreaForYUYV;
      const bestAreaForB = b.format !== 'YUYV' ? bestAreaForJPEG : bestAreaForYUYV;
      return Math.abs(a.area - bestAreaForA) - Math.abs(b.area - bestAreaForB);
    });
    return res[0];
  }
}

export enum ComponentType {
  PhotoWizard,
  SignatureWizard,
  QrReader,
  MrzScanner,
}
